import {
  Bell,
  BookOpen,
  Calendar,
  Calculator,
  ClipboardCheck,
  ClipboardList,
  ClipboardPen,
  CreditCard,
  Globe,
  GraduationCap,
  Home,
  Inbox,
  Layers,
  ListX,
  ListChecks,
  MessageSquareWarning,
  Megaphone,
  Presentation,
  Receipt,
  Rows3,
  ScrollText,
  Shield,
  Star,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { Role } from '@/stores/auth';
import type { PermissionRequirement } from '@/lib/accessControl';

export type NavTarget =
  | '/admin/learning'
  | '/calculator'
  | '/admin/desmos'
  | '/'
  | '/schedule'
  | '/materials'
  | '/homework'
  | '/grades'
  | '/assessments'
  | '/my-mock-exams'
  | '/teacher/groups'
  | '/teacher/schedule'
  | '/teacher/attendance'
  | '/teacher/grades'
  | '/teacher/assessments'
  | '/teacher/mock-exams'
  | '/teacher/homework'
  | '/teacher/materials'
  | '/teacher/lesson'
  | '/teacher/payroll'
  | '/parent/performance'
  | '/parent/behavior'
  | '/parent/schedule'
  | '/parent/attendance'
  | '/parent/balances'
  | '/parent/history'
  | '/parent/notifications'
  | '/admin/people'
  | '/admin/groups'
  | '/admin/materials'
  | '/admin/assessments'
  | '/admin/mock-exams'
  | '/admin/schedule'
  | '/admin/lessons'
  | '/admin/admissions'
  | '/admin/accounting'
  | '/admin/announcements'
  | '/admin/requests'
  | '/admin/payroll'
  | '/leads'
  | '/owner/admins'
  | '/owner/crm-reasons'
  | '/owner/payment-accounts'
  | '/owner/logs'
  | '/owner/site'
  | '/owner/payroll';

export interface NavItem {
  to: NavTarget;
  labelKey: string;
  icon: LucideIcon;
  access?: PermissionRequirement;
}

export type NavTone = 'dashboard' | 'people' | 'learning' | 'funnel' | 'operations' | 'platform';

/** Группа навигации с (опциональным) заголовком-категорией (`nav.sections.*`).
 *  Заголовок показывается только в закреплённом сайдбаре (см. {@link SidebarContent}). */
export interface NavSection {
  /** i18n-ключ заголовка категории; отсутствует → группа без заголовка (напр. «Главная»). */
  labelKey?: string;
  /** Semantic color shared by the section's navigation icons. */
  tone: NavTone;
  items: NavItem[];
}

const DASHBOARD: NavItem = { to: '/', labelKey: 'nav.dashboard', icon: Home };

const ADMIN_SECTIONS: NavSection[] = [
  { tone: 'dashboard', items: [DASHBOARD] },
  {
    labelKey: 'nav.sections.people',
    tone: 'people',
    items: [
      {
        to: '/admin/people',
        labelKey: 'nav.people',
        icon: Users,
        access: {
          any: [
            { obj: 'student', act: 'read' },
            { obj: 'teacher', act: 'read' },
            { obj: 'parent', act: 'read' },
          ],
        },
      },
      {
        to: '/admin/groups',
        labelKey: 'nav.groups',
        icon: Layers,
        access: { all: [{ obj: 'group', act: 'read' }] },
      },
    ],
  },
  {
    labelKey: 'nav.sections.learning',
    tone: 'learning',
    items: [
      {
        to: '/admin/learning',
        labelKey: 'learning.title',
        icon: GraduationCap,
        access: {
          any: [
            { obj: 'learning_content', act: 'read' },
            { obj: 'learning_policy', act: 'read' },
            { obj: 'learning_report', act: 'read' },
          ],
        },
      },
      {
        to: '/admin/materials',
        labelKey: 'nav.materials',
        icon: BookOpen,
        access: { all: [{ obj: 'material', act: 'read' }] },
      },
      {
        to: '/admin/assessments',
        labelKey: 'nav.assessments',
        icon: ListChecks,
        access: { all: [{ obj: 'assessment_template', act: 'read' }] },
      },
      {
        to: '/admin/mock-exams',
        labelKey: 'nav.mockExams',
        icon: ClipboardCheck,
        access: { all: [{ obj: 'mock_exam', act: 'read' }] },
      },
      {
        to: '/admin/desmos',
        labelKey: 'desmos.accessNav',
        icon: Calculator,
        access: { all: [{ obj: 'desmos', act: 'manage' }] },
      },
      {
        to: '/admin/schedule',
        labelKey: 'nav.schedule',
        icon: Calendar,
        access: { all: [{ obj: 'lesson', act: 'read' }] },
      },
      {
        to: '/admin/lessons',
        labelKey: 'nav.lessons',
        icon: Rows3,
        access: { all: [{ obj: 'lesson', act: 'read' }] },
      },
    ],
  },
  {
    labelKey: 'nav.sections.funnel',
    tone: 'funnel',
    items: [
      {
        to: '/admin/admissions',
        labelKey: 'nav.recruitment',
        icon: UserPlus,
        access: {
          any: [
            { obj: 'admission', act: 'read' },
            { obj: 'lead', act: 'read' },
          ],
        },
      },
    ],
  },
  {
    labelKey: 'nav.sections.operations',
    tone: 'operations',
    items: [
      {
        to: '/admin/accounting',
        labelKey: 'nav.accounting',
        icon: Wallet,
        access: { all: [{ obj: 'finance_report', act: 'read' }] },
      },
      {
        to: '/admin/announcements',
        labelKey: 'nav.announcements',
        icon: Megaphone,
        access: { all: [{ obj: 'announcement', act: 'read' }] },
      },
      {
        to: '/admin/requests',
        labelKey: 'nav.requests',
        icon: Inbox,
        access: {
          any: [
            { obj: 'complaint', act: 'read' },
            { obj: 'admin_task', act: 'read' },
            { obj: 'assessment_result', act: 'read' },
          ],
        },
      },
    ],
  },
];

/** Sidebar navigation per role, grouped into labelled sections. */
export const roleNavSections: Record<Role, NavSection[]> = {
  student: [
    { tone: 'dashboard', items: [DASHBOARD] },
    {
      labelKey: 'nav.sections.learning',
      tone: 'learning',
      items: [
        { to: '/schedule', labelKey: 'nav.schedule', icon: Calendar },
        { to: '/materials', labelKey: 'nav.materials', icon: BookOpen },
        { to: '/homework', labelKey: 'nav.homework', icon: ClipboardList },
        { to: '/grades', labelKey: 'nav.grades', icon: Star },
        { to: '/assessments', labelKey: 'nav.assessments', icon: ClipboardPen },
        {
          to: '/calculator',
          labelKey: 'desmos.title',
          icon: Calculator,
          access: { all: [{ obj: 'desmos', act: 'use' }] },
        },
        { to: '/my-mock-exams', labelKey: 'nav.mockExams', icon: ClipboardCheck },
      ],
    },
  ],
  teacher: [
    { tone: 'dashboard', items: [DASHBOARD] },
    {
      labelKey: 'nav.sections.learning',
      tone: 'learning',
      items: [
        { to: '/teacher/groups', labelKey: 'nav.groups', icon: Users },
        { to: '/teacher/schedule', labelKey: 'nav.schedule', icon: Calendar },
        { to: '/teacher/attendance', labelKey: 'nav.attendance', icon: ClipboardCheck },
        { to: '/teacher/grades', labelKey: 'nav.grades', icon: Star },
        { to: '/teacher/assessments', labelKey: 'nav.assessments', icon: ClipboardPen },
        {
          to: '/calculator',
          labelKey: 'desmos.title',
          icon: Calculator,
          access: { all: [{ obj: 'desmos', act: 'use' }] },
        },
        { to: '/teacher/mock-exams', labelKey: 'nav.mockExams', icon: ClipboardCheck },
        { to: '/teacher/homework', labelKey: 'nav.homework', icon: ClipboardList },
        { to: '/teacher/materials', labelKey: 'nav.materials', icon: BookOpen },
        { to: '/teacher/lesson', labelKey: 'nav.lessonMode', icon: Presentation },
      ],
    },
    {
      labelKey: 'nav.sections.operations',
      tone: 'operations',
      items: [{ to: '/teacher/payroll', labelKey: 'payroll.myTitle', icon: Wallet }],
    },
  ],
  parent: [
    { tone: 'dashboard', items: [DASHBOARD] },
    {
      labelKey: 'nav.sections.learning',
      tone: 'learning',
      items: [
        { to: '/parent/performance', labelKey: 'nav.performance', icon: GraduationCap },
        { to: '/my-mock-exams', labelKey: 'nav.mockExams', icon: ClipboardCheck },
        { to: '/parent/behavior', labelKey: 'nav.behavior', icon: MessageSquareWarning },
        { to: '/parent/schedule', labelKey: 'nav.schedule', icon: Calendar },
        { to: '/parent/attendance', labelKey: 'nav.attendance', icon: ClipboardCheck },
      ],
    },
    {
      labelKey: 'nav.sections.operations',
      tone: 'operations',
      items: [
        { to: '/parent/history', labelKey: 'nav.history', icon: Receipt },
        { to: '/parent/notifications', labelKey: 'nav.notifications', icon: Bell },
      ],
    },
  ],
  admin: ADMIN_SECTIONS.map((section) =>
    section.labelKey === 'nav.sections.operations'
      ? {
          ...section,
          items: [
            ...section.items,
            { to: '/admin/payroll', labelKey: 'payroll.myTitle', icon: Wallet },
          ],
        }
      : section,
  ),
  owner: [
    ...ADMIN_SECTIONS,
    {
      labelKey: 'nav.sections.platform',
      tone: 'platform',
      items: [
        { to: '/owner/crm-reasons', labelKey: 'nav.crmReasons', icon: ListX },
        { to: '/owner/admins', labelKey: 'nav.admins', icon: Shield },
        { to: '/owner/payment-accounts', labelKey: 'nav.paymentAccounts', icon: CreditCard },
        { to: '/owner/logs', labelKey: 'nav.logs', icon: ScrollText },
        { to: '/owner/payroll', labelKey: 'payroll.title', icon: Wallet },
        // Stage 11 — landing CMS: single sidebar entry; in-page tabs via SiteSectionNav.
        { to: '/owner/site', labelKey: 'nav.site', icon: Globe },
      ],
    },
  ],
};

const flatten = (sections: NavSection[]): NavItem[] => sections.flatMap((section) => section.items);

/** Flat per-role navigation (Topbar / mobile). Derived from {@link roleNavSections}
 *  so the two never drift. */
export const roleNav: Record<Role, NavItem[]> = {
  student: flatten(roleNavSections.student),
  teacher: flatten(roleNavSections.teacher),
  parent: flatten(roleNavSections.parent),
  admin: flatten(roleNavSections.admin),
  owner: flatten(roleNavSections.owner),
};

export const navAccessByTarget = new Map<NavTarget, PermissionRequirement>(
  Object.values(roleNavSections)
    .flatMap((sections) => sections.flatMap((section) => section.items))
    .filter((item): item is NavItem & { access: PermissionRequirement } => Boolean(item.access))
    .map((item) => [item.to, item.access]),
);
// The legacy bookmark remains protected after its sidebar entry is removed.
navAccessByTarget.set('/leads', { all: [{ obj: 'lead', act: 'read' }] });
